// Interactive sentence builder. Tap a word in the bank to fill the next
// empty slot; tap a filled slot to send the word back to the bank.
// (Click-to-place is more reliable than HTML5 drag on touch devices.)
//
// When the verb token lands in `verbIndex`, the slot pops green to reinforce
// the V2 rule visually, before any "check" press.

import { sentencePuzzles } from '../data/sentences.js';
import { AudioButton } from '../components/AudioButton.js';

let activeIdx = 0;
let placed = []; // token indexes, in placement order

export function GrammarPlayground(root) {
  activeIdx = 0;
  placed = [];
  render(root);
}

function render(root) {
  const puzzle = sentencePuzzles[activeIdx];
  const bankOrder = shuffleAvoidingSolved(puzzle.tokens.map((_, i) => i));

  root.innerHTML = `
    <header class="mb-8">
      <div class="text-[11px] uppercase tracking-[0.18em] text-moss-700 font-semibold">
        Grammar Playground
      </div>
      <h1 class="font-display text-3xl sm:text-4xl font-semibold
                 text-slate2-900 mt-2 tracking-tight">
        Build the sentence
      </h1>
      <p class="mt-3 text-slate2-500 max-w-2xl leading-relaxed">
        Norwegian follows the <strong>V2 rule</strong>: in a main clause the
        finite verb is always the <em>second</em> element. Tap the word blocks
        below in the right order — the verb slot lights up green the moment
        the verb lands in its correct position.
      </p>
    </header>

    <div class="mb-4 flex items-center justify-between text-sm text-slate2-500">
      <div>Puzzle <span class="font-semibold text-slate2-700">${activeIdx + 1}</span>
           of ${sentencePuzzles.length}</div>
      <div class="flex gap-1.5">
        ${sentencePuzzles.map((_, i) => `
          <div class="h-1.5 w-6 rounded-full ${
            i === activeIdx ? 'bg-moss-700'
            : i < activeIdx ? 'bg-moss-300'
            : 'bg-sand-200'
          }"></div>
        `).join('')}
      </div>
    </div>

    <section class="card mb-5">
      <div class="text-[11px] uppercase tracking-wider text-slate2-500 font-semibold mb-2">
        Target meaning
      </div>
      <div class="font-display text-xl text-slate2-900">${puzzle.english}</div>
      <div class="text-xs text-slate2-500 italic mt-1">Literal: ${puzzle.literal}</div>
    </section>

    <section class="card mb-5">
      <div class="text-[11px] uppercase tracking-wider text-slate2-500 font-semibold mb-3">
        Your sentence
      </div>
      <div id="slots" class="flex flex-wrap items-center gap-2 min-h-[64px]">
        ${puzzle.tokens.map((_, i) => slotHtml(i)).join('')}
      </div>
      <div id="feedback" class="mt-4 text-sm min-h-[1.5rem]"></div>
    </section>

    <section class="card mb-6">
      <div class="text-[11px] uppercase tracking-wider text-slate2-500 font-semibold mb-3">
        Word bank
      </div>
      <div id="bank" class="flex flex-wrap gap-2">
        ${bankOrder.map(i => chipHtml(i, puzzle.tokens[i])).join('')}
      </div>
    </section>

    <div class="flex flex-wrap gap-3">
      <button id="reset" class="btn-secondary">Reset</button>
      <button id="check" class="btn-primary">Check answer</button>
      ${activeIdx < sentencePuzzles.length - 1
        ? `<button id="next" class="btn-ghost sm:ml-auto">Skip to next →</button>` : ``}
    </div>
  `;

  wire(root, puzzle, bankOrder);
}

// ─── HTML helpers ────────────────────────────────────────────────────

function slotHtml(i) {
  return `
    <div data-slot="${i}"
         class="slot inline-flex items-center justify-center min-w-[88px] h-12 px-3
                rounded-xl border-2 border-dashed border-sand-300
                text-slate2-400 text-sm font-medium bg-sand-50/60">
      ${i + 1}
    </div>
  `;
}

function chipHtml(tokenIdx, text) {
  return `
    <button data-token="${tokenIdx}"
            class="chip inline-flex items-center px-3.5 py-2 rounded-xl
                   bg-white border border-sand-300 shadow-sm
                   font-display text-lg text-slate2-900
                   hover:border-moss-500 hover:-translate-y-0.5
                   transition-all duration-150">
      ${text}
    </button>
  `;
}

// ─── State plumbing ──────────────────────────────────────────────────

function wire(root, puzzle) {
  const slotsEl   = root.querySelector('#slots');
  const bankEl    = root.querySelector('#bank');
  const feedback  = root.querySelector('#feedback');

  bankEl.addEventListener('click', e => {
    const chip = e.target.closest('[data-token]');
    if (!chip) return;
    if (placed.length >= puzzle.tokens.length) return;
    const tokenIdx = Number(chip.getAttribute('data-token'));
    placed.push(tokenIdx);
    chip.remove();
    paintSlots(puzzle);
    feedback.innerHTML = '';
    if (placed.length === puzzle.tokens.length) checkAnswer(puzzle, feedback);
  });

  slotsEl.addEventListener('click', e => {
    const slot = e.target.closest('[data-slot]');
    if (!slot) return;
    const slotIdx = Number(slot.getAttribute('data-slot'));
    if (slotIdx >= placed.length) return;
    const tokenIdx = placed[slotIdx];
    placed.splice(slotIdx, 1);
    bankEl.insertAdjacentHTML('beforeend', chipHtml(tokenIdx, puzzle.tokens[tokenIdx]));
    paintSlots(puzzle);
    feedback.innerHTML = '';
  });

  root.querySelector('#reset').addEventListener('click', () => {
    placed = [];
    render(root);
  });

  root.querySelector('#check').addEventListener('click', () => checkAnswer(puzzle, feedback));

  const nextBtn = root.querySelector('#next');
  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      activeIdx = Math.min(activeIdx + 1, sentencePuzzles.length - 1);
      placed = [];
      render(root);
    });
  }
}

function paintSlots(puzzle) {
  document.querySelectorAll('#slots [data-slot]').forEach((slot, i) => {
    const tokenIdx = placed[i];
    if (tokenIdx === undefined) {
      slot.className =
        'slot inline-flex items-center justify-center min-w-[88px] h-12 px-3 ' +
        'rounded-xl border-2 border-dashed border-sand-300 ' +
        'text-slate2-400 text-sm font-medium bg-sand-50/60';
      slot.textContent = i + 1;
      return;
    }
    const isVerbSlot   = i === puzzle.verbIndex;
    const isVerbToken  = tokenIdx === puzzle.verbIndex;
    const verbCorrect  = isVerbSlot && isVerbToken;
    const verbMisplaced = isVerbSlot && !isVerbToken;

    slot.className =
      'slot inline-flex items-center justify-center min-w-[88px] h-12 px-3 ' +
      'rounded-xl border-2 font-display text-lg cursor-pointer ' +
      'transition-colors duration-200 ' + (
        verbCorrect
          ? 'border-moss-700 bg-moss-100 text-moss-800 animate-pop'
          : verbMisplaced
            ? 'border-red-300 bg-red-50 text-red-700'
            : 'border-sand-300 bg-white text-slate2-900'
      );
    slot.textContent = puzzle.tokens[tokenIdx];
  });
}

function checkAnswer(puzzle, feedback) {
  if (placed.length < puzzle.tokens.length) {
    feedback.innerHTML = `
      <span class="text-slate2-500">
        Place all ${puzzle.tokens.length} word blocks first.
      </span>`;
    return;
  }
  const built = placed.map(i => puzzle.tokens[i]).join(' ');
  if (built === puzzle.target) {
    feedback.innerHTML = `
      <div class="flex items-start gap-3 p-4 rounded-xl
                  bg-moss-100 border border-moss-300/60 animate-slide-up">
        <span class="text-xl leading-none mt-0.5">✓</span>
        <div class="flex-1 min-w-0">
          <div class="font-medium text-moss-800">Riktig! That's a valid Norwegian sentence.</div>
          <div class="text-sm text-slate2-700 mt-1 leading-relaxed">${puzzle.rule}</div>
        </div>
        ${AudioButton(puzzle.target, { size: 'lg' })}
      </div>
    `;
  } else {
    const verbInRightSlot = placed[puzzle.verbIndex] === puzzle.verbIndex;
    feedback.innerHTML = `
      <div class="flex items-start gap-3 p-4 rounded-xl
                  bg-sand-100 border border-sand-300/60 animate-slide-up">
        <span class="text-xl leading-none mt-0.5">↻</span>
        <div>
          <div class="font-medium text-slate2-700">Not quite — try again.</div>
          <div class="text-sm text-slate2-500 mt-1 leading-relaxed">
            ${verbInRightSlot
              ? 'Your verb is in the right spot — adjust the other words around it.'
              : `Hint: the verb belongs in <strong>slot ${puzzle.verbIndex + 1}</strong> (the V2 rule).`}
          </div>
        </div>
      </div>
    `;
  }
}

// ─── helpers ─────────────────────────────────────────────────────────

function shuffleAvoidingSolved(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  // Don't hand the player an already-solved arrangement.
  if (a.length > 1 && a.every((v, i) => v === arr[i])) {
    [a[0], a[1]] = [a[1], a[0]];
  }
  return a;
}
